// Live HTML approximation of the DOCX output, driven by the same
// TemplateDefinition the renderer consumes and the same SAMPLE_RESUME the
// backend's sample-DOCX endpoint uses.
//
// Pagination: the content is built as a flat list of small atomic blocks
// (a heading, one bullet, one employer row...). A hidden copy is rendered at
// full width to measure each block's real height, then blocks are packed
// into fixed-height pages exactly like Word fills them — when a block does
// not fit inside the bottom margin it moves to the next page instead of
// overlapping it. Section headings are kept with the block that follows so
// no heading is stranded at a page bottom. Word's exact breaks still depend
// on real font metrics, so the Sample DOCX stays the ground truth.
//
// Editing: with `edit` set, every text the resume owns is typed into right
// here (see Editable). The preview never holds its own copy of the resume:
// each edit goes up as a new resume object, the form on the left renders
// the same object, so both sides always agree. Dates and section labels
// are structured/template data and stay with the form and the builder.

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  DEFAULT_HEADING_HIGHLIGHT,
  PAPER_SIZES,
  SAMPLE_RESUME,
  applyCase,
  candidateReportRows,
  consultantRows,
  formatDateRange,
  formatEducationSpan,
  personalDetailEntries,
  splitLineAt,
  backspaceAtStart,
  deleteAtEnd,
  setLine,
  indentLine,
  outdentLine,
  pasteLines,
  lineLevel,
  lineMarker,
  markerGlyph,
  orderedSections,
  splitEducationLine,
  splitCertificationLine,
  parseLanguagesLine,
  setAtPath,
  scaleLabel,
  type AgpResume,
  type ContentLine,
  type ConsultantInfo,
  type CandidateReportInfo,
  type LinesResult,
  type PathSegment,
  type TemplateDefinition,
} from "@agp/shared";
import { Editable, EditSessionContext, type EditSession, type FocusRequest, type LineOps } from "@/components/preview/Editable";
import {
  AppendixPage,
  LoadingAppendixPage,
  RemovedPageBar,
  REMOVED_BAR_HEIGHT,
  type AppendixSource,
  type PageGeometry,
} from "@/components/preview/AppendixPages";
import { SourceTextView } from "@/components/preview/SourceTextView";
import { setCaret } from "@/lib/caret";

// twips → px at 96dpi (1440 twips = 1in = 96px). Exported for JdPreview,
// which shares these mechanics.
export const tw = (twips: number) => (twips * 96) / 1440;
// pt → px.
export const pt = (points: number) => (points * 96) / 72;

// The experience/education grid and list indents of docx.ts, in pixels:
// LOCATION_COL_TWIPS, CELL_PAD, and 340 twips of indent per list level
// with the marker hanging halfway.
const LOC_COL_PX = tw(1900);
const CELL_PAD_PX = tw(60);
const LIST_STEP_PX = tw(340);

const PAGE_GAP = 18;
const MAX_PAGES = 12;
const FOCUS_TTL_MS = 1500;

const FONT_STACKS: Record<string, string> = {
  Calibri: "Calibri, Carlito, 'Helvetica Neue', Arial, sans-serif",
  Cambria: "Cambria, Georgia, serif",
  Georgia: "Georgia, 'Times New Roman', serif",
  Garamond: "Garamond, 'Apple Garamond', 'EB Garamond', serif",
  Arial: "Arial, Helvetica, sans-serif",
  "Times New Roman": "'Times New Roman', Times, serif",
  Verdana: "Verdana, Geneva, sans-serif",
  Tahoma: "Tahoma, Geneva, sans-serif",
  "Book Antiqua": "'Book Antiqua', Palatino, 'Palatino Linotype', serif",
  "Palatino Linotype": "'Palatino Linotype', Palatino, serif",
};

export function fontStack(name: string): string {
  return FONT_STACKS[name] ?? `'${name}', sans-serif`;
}

export type JumpTarget =
  | { tab: "experience" | "education"; index: number }
  | { tab: "candidate" };

export interface PreviewEdit {
  /** A new resume after an edit in the preview. `key` names the field for
   *  undo grouping (null for structural edits, which always get a step). */
  onChange: (next: AgpResume, key: string | null) => void;
  onUndo: () => void;
  onRedo: () => void;
  /** The reviewer clicked something only the form edits (dates). */
  onJump?: (target: JumpTarget) => void;
}

interface EditCtx {
  commit: (next: AgpResume, focus: FocusRequest | null, key: string | null) => void;
  jump: (target: JumpTarget) => void;
  session: EditSession;
}

interface Block {
  node: React.ReactNode;
  /** Never end a page with this block (section headings). */
  keepWithNext?: boolean;
}

const emptyConsultant = (): ConsultantInfo => ({ preparedBy: null, telephone: null, email: null, date: null });
const emptyReport = (): CandidateReportInfo => ({ client: null, position: null });

// All spacing inside blocks uses padding, never margin — margins don't show
// up in getBoundingClientRect and would make measured heights lie.
function buildBlocks(
  d: TemplateDefinition,
  resume: AgpResume,
  includeContact: boolean,
  photoUrl: string | null,
  photoPlaceholder: boolean,
  ed: EditCtx | null,
): Block[] {
  const heading = `#${d.colors.heading}`;
  const subtle = `#${d.colors.subtle}`;
  const headingFont = fontStack(d.fonts.heading);
  const dateColPx = tw(d.dateColumnTwips);
  const blocks: Block[] = [];
  let key = 0;

  const push = (node: React.ReactNode, keepWithNext?: boolean) =>
    blocks.push({ node, keepWithNext });

  // ---- editing helpers ------------------------------------------------------
  const ro = ed == null;
  const nullable = (t: string) => (t.trim() === "" ? null : t);
  const setPath =
    (path: PathSegment[], keepEmpty = false) =>
    (t: string): AgpResume =>
      setAtPath(resume, path, keepEmpty ? t : nullable(t));

  /** One value the reviewer types into; Enter just leaves the field. */
  const field = (
    id: string,
    value: string | null,
    set: (text: string) => AgpResume,
    opts: { block?: boolean; style?: CSSProperties; placeholder?: string } = {},
  ) => (
    <Editable
      id={id}
      value={value ?? ""}
      readOnly={ro}
      block={opts.block ?? true}
      style={opts.style}
      placeholder={opts.placeholder}
      onCommit={(t, caret) => ed?.commit(set(t), { id, offset: caret }, id)}
    />
  );

  const jumpable = (target: JumpTarget, node: React.ReactNode, title: string) =>
    ed ? (
      <span data-jump="" title={title} onClick={() => ed.jump(target)}>
        {node}
      </span>
    ) : (
      node
    );

  /** Word-like keys for one line of a list. `plain` lists (skills, school
   *  bullets) have no line kinds, so Tab does nothing there and Enter on an
   *  empty item is ignored rather than turning it into a paragraph. */
  const lineOps = (
    idPrefix: string,
    lines: ContentLine[],
    toResume: (lines: ContentLine[]) => AgpResume,
    i: number,
    plain: boolean,
  ): LineOps | undefined => {
    if (!ed) return undefined;
    const line = lines[i]!;
    const apply = (r: LinesResult) =>
      ed.commit(toResume(r.lines), { id: `${idPrefix}.${r.focus.index}`, offset: r.focus.offset }, null);
    const go = (j: number, offset: number) => {
      if (j >= 0 && j < lines.length) ed.session.requestFocus({ id: `${idPrefix}.${j}`, offset });
      else ed.session.focusRelative(`${idPrefix}.${i}`, j < 0 ? "prev" : "next", offset);
    };
    return {
      onSplit: (offset) => {
        if (plain && line.text === "") return;
        apply(splitLineAt(lines, i, offset));
      },
      onBackspaceAtStart: () => {
        if (plain) {
          if (i > 0) apply(deleteAtEnd(lines, i - 1)!);
          return;
        }
        const r = backspaceAtStart(lines, i);
        if (r) apply(r);
      },
      onDeleteAtEnd: () => {
        const r = deleteAtEnd(lines, i);
        if (r) apply(r);
      },
      onIndent: (offset) => {
        if (!plain) apply(setLine(lines, i, indentLine(line), offset));
      },
      onOutdent: (offset) => {
        if (!plain) apply(setLine(lines, i, outdentLine(line), offset));
      },
      onPasteLines: (offset, pasted) =>
        apply(pasteLines(lines, i, offset, plain ? pasted.map((p) => ({ ...p, kind: null })) : pasted)),
      onArrow: (direction, offset) => go(direction === "prev" ? i - 1 : i + 1, offset),
    };
  };

  const hs = d.headingStyle;
  // No headingStyle block at all → grey highlight, the AGP-preferred look
  // (mirrors Builder.sectionHeading in docx.ts). Explicit null → none.
  const headingHighlight = hs ? hs.highlight : DEFAULT_HEADING_HIGHLIGHT;
  const headingCase: CSSProperties["textTransform"] =
    hs?.case === "none" ? "none" : hs?.case === "title" ? "capitalize" : "uppercase";
  const sectionHeading = (label: string, editable?: React.ReactNode) =>
    push(
      <div key={key++} style={{ paddingTop: 12, paddingBottom: 7 }}>
        <div
          style={{
            fontFamily: headingFont,
            color: heading,
            fontWeight: 700,
            fontSize: pt(d.fonts.headingPt),
            borderBottom: hs?.showRule === false ? undefined : `1px solid ${heading}`,
            background: headingHighlight ? `#${headingHighlight}` : undefined,
            paddingBottom: 2,
            paddingLeft: headingHighlight ? 4 : 0,
            textTransform: editable ? headingCase : undefined,
          }}
        >
          {editable ?? applyCase(label, hs?.case)}
        </div>
      </div>,
      true,
    );

  /** Bold label | value rows whose values line up with the experience
   *  grid's content column (docx.ts labeledRows). */
  const labeledRows = (
    rows: Array<{ id: string; label: string; value: string; set: (t: string) => AgpResume }>,
  ) => {
    for (const r of rows) {
      push(
        <div
          key={key++}
          style={{ display: "grid", gridTemplateColumns: `${dateColPx}px 1fr`, paddingBottom: 2 }}
        >
          <b>{r.label}</b>
          {field(r.id, r.value, r.set)}
        </div>,
      );
    }
  };

  // Mirrors the numbering definitions in docx.ts: 17pt per level of indent,
  // the marker in the hanging space. Lines inside the grid (indented to
  // the content column) stop short of the right edge like their cell.
  const bulletRow = (content: React.ReactNode, indentPx = 0, level = 0, glyph = "•") => {
    const indent = indentPx + level * LIST_STEP_PX;
    push(
      <div key={key++} style={{ position: "relative", paddingLeft: indent + LIST_STEP_PX, paddingRight: indentPx > 0 ? CELL_PAD_PX : 0, paddingBottom: 2 }}>
        <span
          style={{
            position: "absolute",
            left: indent + LIST_STEP_PX / 2,
            ...(d.fonts.bulletPt ? { fontSize: pt(d.fonts.bulletPt), lineHeight: 1 } : {}),
          }}
        >
          {glyph}
        </span>
        {content}
      </div>,
    );
  };

  // Mixed content lines (mirrors Builder.lines in docx.ts): plain paragraphs,
  // bullets and one level of sub-bullets.
  const contentLines = (
    list: ContentLine[],
    indentPx: number,
    idPrefix: string,
    toResume: (lines: ContentLine[]) => AgpResume,
  ) => {
    // Numbered markers count within their list and level, restarting after
    // a paragraph, the way Word numbers them.
    const counts: number[] = [];
    list.forEach((l, i) => {
      const id = `${idPrefix}.${i}`;
      const text = (
        <Editable
          id={id}
          value={l.text}
          readOnly={ro}
          lineOps={lineOps(idPrefix, list, toResume, i, false)}
          onCommit={(t, caret) =>
            ed?.commit(toResume(list.map((x, k) => (k === i ? { ...x, text: t } : x))), { id, offset: caret }, id)
          }
        />
      );
      if (l.kind === "para") {
        counts.length = 0;
        // Mirrors Builder.lines: a paragraph after list lines gets space
        // above so details read as separate blocks.
        const afterList = i > 0 && list[i - 1]!.kind !== "para";
        push(
          <div key={key++} style={{ paddingLeft: indentPx, paddingRight: indentPx > 0 ? CELL_PAD_PX : 0, paddingBottom: 2, paddingTop: afterList ? 6 : 0 }}>
            {text}
          </div>,
        );
      } else {
        const level = lineLevel(l);
        counts[level] = (counts[level] ?? 0) + 1;
        counts.length = level + 1;
        bulletRow(text, indentPx, level, markerGlyph(lineMarker(l), counts[level]!));
      }
    });
  };

  /** Lists of plain strings (skills, school bullets, certifications as
   *  composed lines) edited with the same keys, minus line kinds. */
  const plainLines = (
    list: string[],
    indentPx: number,
    idPrefix: string,
    toResume: (items: string[]) => AgpResume,
  ) => {
    const lines: ContentLine[] = list.map((text) => ({ text, kind: "bullet" }));
    const back = (next: ContentLine[]) => toResume(next.map((l) => l.text));
    list.forEach((text, i) => {
      const id = `${idPrefix}.${i}`;
      bulletRow(
        <Editable
          id={id}
          value={text}
          readOnly={ro}
          lineOps={lineOps(idPrefix, lines, back, i, true)}
          onCommit={(t, caret) => ed?.commit(toResume(list.map((x, k) => (k === i ? t : x))), { id, offset: caret }, id)}
        />,
        indentPx,
      );
    });
  };

  const c = resume.candidate;

  // Candidate name (with a photo placeholder beside it when the template
  // shows candidate photos — real photos come from each resume job).
  const nameCase = d.nameCase ?? "upper";
  const nameEl = (
    <div
      style={{
        fontFamily: headingFont,
        color: heading,
        fontWeight: 700,
        fontSize: pt(d.fonts.namePt),
        lineHeight: 1.1,
        textTransform: nameCase === "upper" ? "uppercase" : "none",
      }}
    >
      {field("candidate.fullName", c.fullName, setPath(["candidate", "fullName"]), { placeholder: "Candidate name" })}
    </div>
  );
  if (d.photo?.show && (photoUrl || photoPlaceholder)) {
    // The builder has no real job, so it shows the neutral placeholder box.
    // On the review screen a job WITHOUT a photo renders with no box at all,
    // exactly like the DOCX output — the template only says where a photo
    // goes when one exists.
    const photoBox = photoUrl ? (
      <img
        src={photoUrl}
        alt=""
        style={{
          width: d.photo.widthPx,
          height: d.photo.heightPx,
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    ) : (
      <div
        style={{
          width: d.photo.widthPx,
          height: d.photo.heightPx,
          background: "#CBD0D6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#7A828B",
          fontFamily: "system-ui, sans-serif",
          fontSize: 11,
          flexShrink: 0,
        }}
      >
        photo
      </div>
    );
    {
      // Mirrors the DOCX layout. Beside-the-name: a LEFT photo has the name
      // sitting directly beside it (top-right stays free for the logo); a
      // RIGHT photo keeps the name on the left edge. Above/below variants
      // are full-width rows with their own alignment.
      const pos = d.photo.position ?? "right";
      if (pos === "left" || pos === "right") {
        push(
          <div
            key={key++}
            style={{
              display: "flex",
              justifyContent: pos === "left" ? "flex-start" : "space-between",
              alignItems: "flex-start",
              gap: 12,
              paddingBottom: 8,
            }}
          >
            {pos === "left" ? photoBox : <div style={{ flex: 1 }}>{nameEl}</div>}
            {pos === "left" ? <div style={{ flex: 1 }}>{nameEl}</div> : photoBox}
          </div>,
          true,
        );
      } else {
        const photoRow = (
          <div
            key={key++}
            style={{
              display: "flex",
              justifyContent: pos.endsWith("-center")
                ? "center"
                : pos.endsWith("-right")
                  ? "flex-end"
                  : "flex-start",
              paddingBottom: 8,
            }}
          >
            {photoBox}
          </div>
        );
        const nameRow = (
          <div key={key++} style={{ paddingBottom: 8 }}>
            {nameEl}
          </div>
        );
        if (pos.startsWith("above")) {
          push(photoRow, true);
          push(nameRow, true);
        } else {
          push(nameRow, true);
          push(photoRow, true);
        }
      }
    }
  } else {
    push(
      <div key={key++} style={{ paddingBottom: 8 }}>
        {nameEl}
      </div>,
      true,
    );
  }

  for (const section of orderedSections(d.sections, resume.sectionOrder)) {
    switch (section.key) {
      case "candidateReport": {
        // Mirrors docx.ts: labels always render; empty values are the blank
        // lines the consultant fills in after generation.
        sectionHeading(section.label);
        const r = resume.candidateReport ?? emptyReport();
        labeledRows(
          candidateReportRows(resume).map(([label, value], i) => {
            const k = (["client", "position"] as const)[i]!;
            return {
              id: `candidateReport.${k}`,
              label,
              value,
              set: (t) => ({ ...resume, candidateReport: { ...r, [k]: nullable(t) } }),
            };
          }),
        );
        break;
      }
      case "personalDetails": {
        const entries = personalDetailEntries(resume, d, includeContact);
        if (entries.length === 0) break;
        sectionHeading(section.label);
        labeledRows(
          entries.map((e) => ({
            id: `candidate.${e.key}`,
            label: e.label,
            value: e.value,
            set:
              e.key === "languages"
                ? (t) => ({ ...resume, candidate: { ...c, languages: parseLanguagesLine(t, d.languageFormat) } })
                : setPath(["candidate", e.key]),
          })),
        );
        break;
      }
      case "consultantSummary": {
        sectionHeading(section.label);
        const con = resume.consultant ?? emptyConsultant();
        labeledRows(
          consultantRows(resume).map(([label, value], i) => {
            const k = (["preparedBy", "telephone", "email", "date"] as const)[i]!;
            return {
              id: `consultant.${k}`,
              label,
              value,
              set: (t) => ({ ...resume, consultant: { ...con, [k]: nullable(t) } }),
            };
          }),
        );
        break;
      }
      case "summary": {
        if (c.summaryBullets.length === 0) break;
        sectionHeading(section.label);
        contentLines(c.summaryBullets, 0, "candidate.summaryBullets", (lines) => ({
          ...resume,
          candidate: { ...c, summaryBullets: lines },
        }));
        break;
      }
      case "education": {
        if (resume.education.length === 0) break;
        sectionHeading(section.label);
        resume.education.forEach((e, i) => {
          push(
            <div
              key={key++}
              style={{
                display: "grid",
                gridTemplateColumns: `${dateColPx}px 1fr ${LOC_COL_PX}px`,
                paddingTop: i > 0 ? 6 : 0,
                // Optional rule under each school row (mirrors educationRows
                // in docx.ts).
                borderBottom:
                  d.educationStyle?.schoolRule === true ? `1px solid ${heading}` : undefined,
                paddingBottom: d.educationStyle?.schoolRule === true ? 2 : 0,
              }}
            >
              <b style={{ paddingRight: CELL_PAD_PX }}>
                {jumpable(
                  { tab: "education", index: i },
                  formatEducationSpan(e, d.dateFormat),
                  "Dates are edited in the Education form (click to go there)",
                )}
              </b>
              <b style={{ paddingRight: CELL_PAD_PX }}>{field(`education.${i}.institution`, e.institution, setPath(["education", i, "institution"]))}</b>
              <b>
                {field(`education.${i}.location`, e.location, setPath(["education", i, "location"]), {
                  style: { textAlign: "right" },
                })}
              </b>
            </div>,
            true,
          );
          const line2 = [
            [e.qualification, e.field].filter(Boolean).join(", "),
            e.honours,
          ]
            .filter(Boolean)
            .join(" — ");
          if (line2) {
            push(
              <div key={key++} style={{ paddingLeft: dateColPx, paddingRight: CELL_PAD_PX, paddingBottom: 2 }}>
                <b>
                  {field(`education.${i}.line2`, line2, (t) =>
                    setAtPath(resume, ["education", i], { ...e, ...splitEducationLine(t) }),
                  )}
                </b>
              </div>,
            );
          }
          plainLines(e.bullets, dateColPx, `education.${i}.bullets`, (bullets) =>
            setAtPath(resume, ["education", i, "bullets"], bullets),
          );
        });
        break;
      }
      case "experience": {
        if (resume.experience.length === 0) break;
        sectionHeading(section.label);
        resume.experience.forEach((entry, i) => {
          const isCurrent =
            entry.end?.isPresent === true || entry.roles.some((r) => r.end?.isPresent === true);
          const emphasis = isCurrent ? d.currentEmployment : undefined;
          const empStyle: React.CSSProperties = {
            paddingRight: CELL_PAD_PX,
            textDecoration: emphasis?.underline ? "underline" : undefined,
            color: emphasis?.useHeadingColor ? heading : undefined,
          };
          push(
            <div
              key={key++}
              style={{
                display: "grid",
                gridTemplateColumns: `${dateColPx}px 1fr ${LOC_COL_PX}px`,
                paddingTop: i > 0 ? 10 : 0,
                // Continuous rule under the whole employer row (default on),
                // mirroring the cell bottom borders in docx.ts.
                borderBottom:
                  d.experienceStyle?.employerRule !== false ? `1px solid ${heading}` : undefined,
                paddingBottom: d.experienceStyle?.employerRule !== false ? 2 : 0,
              }}
            >
              <b style={empStyle}>
                {jumpable(
                  { tab: "experience", index: i },
                  formatDateRange(entry.start, entry.end, d.dateFormat),
                  "Dates are edited in the Experience form (click to go there)",
                )}
              </b>
              <b style={empStyle}>
                {field(`experience.${i}.employer`, entry.employer, setPath(["experience", i, "employer"]), {
                  placeholder: "Employer",
                })}
              </b>
              <b>
                {field(`experience.${i}.employerLocation`, entry.employerLocation, setPath(["experience", i, "employerLocation"]), {
                  style: { textAlign: "right" },
                })}
              </b>
            </div>,
            true,
          );
          // Company description (plain paragraph, never bulleted) then the
          // 規模 line, under the first role title — mirrors experienceRows
          // in docx.ts.
          const multiRole = entry.roles.length > 1;
          entry.roles.forEach((r, ri) => {
            const hasNotes = ri === 0 && (!!entry.employerDescription || !!entry.scale);
            if (r.title || multiRole) {
              push(
                <div
                  key={key++}
                  style={{
                    display: "grid",
                    gridTemplateColumns: r.location ? `${dateColPx}px 1fr ${LOC_COL_PX}px` : `${dateColPx}px 1fr`,
                    paddingTop: multiRole && ri > 0 ? 4 : 0,
                    paddingBottom: 1,
                  }}
                >
                  <span style={{ color: subtle, paddingRight: CELL_PAD_PX }}>
                    {multiRole
                      ? jumpable(
                          { tab: "experience", index: i },
                          formatDateRange(r.start, r.end, d.dateFormat),
                          "Role dates are edited in the Experience form (click to go there)",
                        )
                      : ""}
                  </span>
                  <span
                    style={{
                      fontWeight: 700,
                      fontStyle: d.experienceStyle?.titleItalic === true ? "italic" : undefined,
                      paddingRight: CELL_PAD_PX,
                    }}
                  >
                    {field(`experience.${i}.roles.${ri}.title`, r.title, setPath(["experience", i, "roles", ri, "title"]), {
                      placeholder: "Job title",
                    })}
                  </span>
                  {r.location && (
                    <b>
                      {field(`experience.${i}.roles.${ri}.location`, r.location, setPath(["experience", i, "roles", ri, "location"]), {
                        style: { textAlign: "right" },
                      })}
                    </b>
                  )}
                </div>,
                hasNotes || r.bullets.length > 0,
              );
            }
            if (ri === 0 && entry.employerDescription) {
              push(
                <div key={key++} style={{ paddingLeft: dateColPx, paddingRight: CELL_PAD_PX, paddingBottom: entry.scale ? 2 : 8 }}>
                  {field(`experience.${i}.employerDescription`, entry.employerDescription, setPath(["experience", i, "employerDescription"]))}
                </div>,
              );
            }
            if (ri === 0 && entry.scale) {
              push(
                <div key={key++} style={{ paddingLeft: dateColPx, paddingRight: CELL_PAD_PX, paddingBottom: 8 }}>
                  {scaleLabel(entry.scale)}:{" "}
                  {field(`experience.${i}.scale`, entry.scale, setPath(["experience", i, "scale"]), { block: false })}
                </div>,
              );
            }
            contentLines(r.bullets, dateColPx, `experience.${i}.roles.${ri}.bullets`, (lines) =>
              setAtPath(resume, ["experience", i, "roles", ri, "bullets"], lines),
            );
          });
        });
        break;
      }
      case "coreCompetencies": {
        if (resume.coreCompetencies.length === 0) break;
        sectionHeading(section.label);
        plainLines(resume.coreCompetencies, 0, "coreCompetencies", (coreCompetencies) => ({ ...resume, coreCompetencies }));
        break;
      }
      case "technicalSkills": {
        if (resume.technicalSkills.length === 0) break;
        sectionHeading(section.label);
        if (d.emphasizeAchievements) {
          push(
            <div key={key++} style={{ paddingBottom: 2 }}>
              {field("technicalSkills.line", resume.technicalSkills.join(", "), (t) => ({
                ...resume,
                technicalSkills: t.split(",").map((s) => s.trim()).filter(Boolean),
              }))}
            </div>,
          );
        } else {
          plainLines(resume.technicalSkills, 0, "technicalSkills", (technicalSkills) => ({ ...resume, technicalSkills }));
        }
        break;
      }
      case "certifications": {
        if (resume.certifications.length === 0) break;
        sectionHeading(section.label);
        plainLines(
          resume.certifications.map((cert) =>
            [cert.name, cert.issuer, cert.year ? String(cert.year) : null].filter(Boolean).join(" — "),
          ),
          0,
          "certifications",
          (items) => ({
            ...resume,
            certifications: items.map((s, k) => ({
              ...splitCertificationLine(s),
              confidence: resume.certifications[k]?.confidence ?? 1,
            })),
          }),
        );
        break;
      }
      case "publications": {
        if (resume.publications.length === 0) break;
        sectionHeading(section.label);
        plainLines(resume.publications, 0, "publications", (publications) => ({ ...resume, publications }));
        break;
      }
      case "careerInterests": {
        if (resume.careerInterests.length === 0) break;
        sectionHeading(section.label);
        plainLines(resume.careerInterests, 0, "careerInterests", (careerInterests) => ({ ...resume, careerInterests }));
        break;
      }
      case "otherSections": {
        resume.otherSections.forEach((s, i) => {
          if (s.bullets.length === 0) return;
          sectionHeading(
            s.heading,
            field(`otherSections.${i}.heading`, s.heading, setPath(["otherSections", i, "heading"], true), { block: false }),
          );
          contentLines(s.bullets, 0, `otherSections.${i}.bullets`, (lines) =>
            setAtPath(resume, ["otherSections", i, "bullets"], lines),
          );
        });
        break;
      }
    }
  }
  return blocks;
}

/** Greedy page packing, Word-style: a block that would cross the bottom
 *  margin moves whole to the next page; keep-with-next blocks (headings)
 *  never end a page. Returns block-index ranges per page. Exported for
 *  JdPreview. */
export function paginate(
  heights: number[],
  keepWithNext: boolean[],
  capacity: number,
  // Page 1 can be shorter than the rest (JD: the logo pushes its content
  // down, later pages start at the top margin).
  firstCapacity: number = capacity,
): number[][] {
  const pages: number[][] = [];
  let page: number[] = [];
  let used = 0;

  const closePage = () => {
    // Never leave keep-with-next blocks (heading chains) at a page bottom —
    // pull them onto the next page along with everything after them.
    const carried: number[] = [];
    while (page.length > 1 && keepWithNext[page[page.length - 1]!]) {
      carried.unshift(page.pop()!);
    }
    pages.push(page);
    page = carried;
    used = carried.reduce((sum, i) => sum + (heights[i] ?? 0), 0);
  };

  for (let i = 0; i < heights.length; i++) {
    const h = heights[i] ?? 0;
    const cap = pages.length === 0 ? firstCapacity : capacity;
    if (used + h > cap && page.length > 0) closePage();
    page.push(i);
    used += h;
    if (pages.length >= MAX_PAGES) break;
  }
  if (page.length > 0) pages.push(page);
  return pages.slice(0, MAX_PAGES);
}

type AppendixItem =
  | { kind: "page"; source: AppendixSource; index: number }
  | { kind: "removed"; source: AppendixSource; index: number }
  | { kind: "loading"; source: AppendixSource }
  | { kind: "text"; source: AppendixSource };

function appendixItems(appendix: AppendixSource[]): AppendixItem[] {
  const items: AppendixItem[] = [];
  for (const source of appendix) {
    if (source.view === "text" && source.text) {
      items.push({ kind: "text", source });
      continue;
    }
    if (!source.pages) {
      items.push({ kind: "loading", source });
      continue;
    }
    const removed = new Set(source.redactions.removedPages);
    for (const page of source.pages) {
      items.push({ kind: removed.has(page.index) ? "removed" : "page", source, index: page.index });
    }
  }
  return items;
}

export function TemplatePreview({
  definition,
  logoUrl,
  watermarkUrl,
  includeContact = true,
  resume = SAMPLE_RESUME,
  photoUrl = null,
  photoPlaceholder = false,
  zoom = 1,
  appendix = [],
  edit,
}: {
  definition: TemplateDefinition;
  logoUrl: string | null;
  watermarkUrl: string | null;
  includeContact?: boolean;
  resume?: AgpResume;
  /** Real candidate photo (review screen); null means no photo. */
  photoUrl?: string | null;
  /** Show a grey placeholder box when there is no photoUrl (builder preview,
   *  which has no real job). The review screen leaves this off: a job with
   *  no photo previews without one, like the DOCX output. */
  photoPlaceholder?: boolean;
  /** Multiplies the fit-to-width scale; >1 overflows into horizontal scroll. */
  zoom?: number;
  /** Originals attached after the report, page by page. */
  appendix?: AppendixSource[];
  /** Enables typing straight into the preview. */
  edit?: PreviewEdit;
}) {
  const d = definition;
  const paper = PAPER_SIZES[d.page.size ?? "A4"];
  const PAGE_W = tw(paper.widthTwips);
  const PAGE_H = tw(paper.heightTwips);
  const contentW = PAGE_W - tw(d.page.marginLeft) - tw(d.page.marginRight);
  // In "floating" logo layout (the default) the logo is anchored inside the
  // top margin and never moves the body — the candidate name stays level with
  // it. In "inline" layout the body must start BELOW the header — Word pushes
  // body text down when a header outgrows the top margin, and the preview
  // mirrors that.
  const headerTop = Math.max(10, tw(d.page.marginTop) - 34);
  const logoFloating = (d.branding.logoLayout ?? "floating") === "floating";
  const logoDisplayH = logoUrl ? d.branding.logoSize.height : 18;
  const contentTop =
    logoFloating || !logoUrl
      ? tw(d.page.marginTop)
      : Math.max(tw(d.page.marginTop), headerTop + logoDisplayH + 8);
  const capacity = PAGE_H - contentTop - tw(d.page.marginBottom);

  const wrapRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.7);
  const [pages, setPages] = useState<number[][]>([]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / PAGE_W) * zoom);
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, [PAGE_W, zoom]);

  // ---- editing session ------------------------------------------------------
  // The caret has to survive re-renders: every edit rebuilds the blocks and
  // may move a line to another page (a different DOM parent), so the edit
  // says where the caret belongs and the layout effect below puts it there.
  const pending = useRef<(FocusRequest & { at: number }) | null>(null);
  const lastFocus = useRef<FocusRequest | null>(null);
  const editRef = useRef(edit);
  editRef.current = edit;

  const focusNow = (req: FocusRequest) => {
    const root = visibleRef.current;
    const el = root?.querySelector<HTMLElement>(`[data-edit-id="${CSS.escape(req.id)}"]`);
    if (!el) return false;
    if (document.activeElement !== el) el.focus({ preventScroll: true });
    setCaret(el, req.offset);
    el.scrollIntoView({ block: "nearest" });
    lastFocus.current = req;
    return true;
  };

  const session = useMemo<EditSession>(
    () => ({
      requestFocus: (req) => {
        pending.current = { ...req, at: Date.now() };
        focusNow(req);
      },
      noteFocus: (req) => {
        lastFocus.current = req;
      },
      focusRelative: (fromId, direction, offset) => {
        const root = visibleRef.current;
        if (!root) return;
        const all = [...root.querySelectorAll<HTMLElement>("[data-edit-id]")];
        const i = all.findIndex((el) => el.dataset.editId === fromId);
        const target = all[direction === "prev" ? i - 1 : i + 1];
        if (!target) return;
        session.requestFocus({ id: target.dataset.editId!, offset });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const editCtx = useMemo<EditCtx | null>(
    () =>
      edit
        ? {
            commit: (next, focus, key) => {
              if (focus) {
                pending.current = { ...focus, at: Date.now() };
                lastFocus.current = focus;
              }
              editRef.current?.onChange(next, key);
            },
            jump: (target) => editRef.current?.onJump?.(target),
            session,
          }
        : null,
    [edit != null, session],
  );

  // Put the caret where the last edit asked for, on every render for a
  // short while (the first render may not include a brand-new line yet:
  // pages are packed only after the hidden copy has been measured).
  useLayoutEffect(() => {
    const req = pending.current;
    if (!req) return;
    if (Date.now() - req.at > FOCUS_TTL_MS) {
      pending.current = null;
      return;
    }
    focusNow(req);
  });

  const onRootKeyDown = (e: React.KeyboardEvent) => {
    if (!edit) return;
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    // Leave the field first so its text is refreshed from the restored
    // resume (a focused field keeps what was typed), then come back to it.
    const restore = () => {
      if (lastFocus.current) pending.current = { ...lastFocus.current, at: Date.now() };
      (document.activeElement as HTMLElement | null)?.blur();
    };
    if (k === "z") {
      e.preventDefault();
      restore();
      if (e.shiftKey) edit.onRedo();
      else edit.onUndo();
    } else if (k === "y" && e.ctrlKey) {
      e.preventDefault();
      restore();
      edit.onRedo();
    }
  };

  const staticBlocks = buildBlocks(d, resume, includeContact, photoUrl, photoPlaceholder, null);
  const liveBlocks = editCtx
    ? buildBlocks(d, resume, includeContact, photoUrl, photoPlaceholder, editCtx)
    : staticBlocks;
  const keepWithNext = staticBlocks.map((b) => b.keepWithNext === true);

  // Measure the hidden copy, then pack pages. Re-runs on every definition
  // change and when webfonts finish loading (ResizeObserver fires on reflow).
  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const measure = () => {
      const heights = [...el.children].map((child) => child.getBoundingClientRect().height);
      setPages(paginate(heights, keepWithNext, capacity));
    };
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(d), includeContact, resume, photoUrl, capacity, contentW]);

  const bodyFont = fontStack(d.fonts.body);
  const headingFont = fontStack(d.fonts.heading);
  const heading = `#${d.colors.heading}`;
  const text = `#${d.colors.text}`;
  const subtle = `#${d.colors.subtle}`;

  const bodyStyle: React.CSSProperties = {
    color: text,
    fontFamily: bodyFont,
    fontSize: pt(d.fonts.bodyPt),
    lineHeight: 1.35,
  };

  // Watermark anchors to the page like Word's floating image does.
  const wmH = d.branding.watermarkHAlign ?? "center";
  const wmV = d.branding.watermarkVAlign ?? "center";
  // Width AND height, like the logo: Word stretches the floating image to
  // exactly watermarkSize, so the preview must show the same. Sizes are px
  // at 96dpi — the DOCX embeds at that scale (docx ImageRun: px × 9525 EMU)
  // and this page is drawn at it too.
  const wmStyle: React.CSSProperties = {
    position: "absolute",
    width: d.branding.watermarkSize.width,
    height: d.branding.watermarkSize.height,
    pointerEvents: "none",
  };
  if (wmH === "center") wmStyle.left = "50%";
  else if (wmH === "left") wmStyle.left = 0;
  else wmStyle.right = 0;
  if (wmV === "center") wmStyle.top = "50%";
  else if (wmV === "top") wmStyle.top = 0;
  else wmStyle.bottom = 0;
  wmStyle.transform = `translate(${wmH === "center" ? "-50%" : "0"}, ${wmV === "center" ? "-50%" : "0"})`;

  // Header chrome sits inside the top margin (as Word headers do). The
  // watermark and footer repeat on every page; the logo/company name shows
  // on page 1 ONLY (mirrors titlePage: true in docx.ts — a repeating logo
  // would overlap the body of pages 2+).
  const pageChrome = (pi: number) => (
    <>
      {watermarkUrl && (
        <img
          src={watermarkUrl}
          alt=""
          style={{ ...wmStyle, opacity: d.branding.watermarkOpacity ?? 1 }}
        />
      )}
      {pi === 0 && (
        <div
          style={{
            position: "absolute",
            top: headerTop,
            left: tw(d.page.marginLeft),
            right: tw(d.page.marginRight),
            // Flex, not text-align: Tailwind's base reset makes <img> a block
            // element, which silently ignores text-align — the logo stuck to
            // the left no matter what alignment was picked.
            display: "flex",
            justifyContent: { left: "flex-start", center: "center", right: "flex-end" }[
              d.branding.logoAlign ?? "right"
            ],
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="logo"
              style={{
                // Word stretches the image to exactly logoSize — show the same,
                // including any aspect-ratio distortion, so it isn't a surprise
                // in the document.
                width: d.branding.logoSize.width,
                height: d.branding.logoSize.height,
                opacity: d.branding.logoOpacity ?? 1,
              }}
            />
          ) : (
            <span
              style={{ fontFamily: headingFont, color: heading, fontWeight: 700, fontSize: pt(10) }}
            >
              {d.branding.companyName}
            </span>
          )}
        </div>
      )}
      <div
        style={{
          position: "absolute",
          bottom: 14,
          left: tw(d.page.marginLeft),
          right: tw(d.page.marginRight),
          textAlign: "center",
          color: `#${d.branding.footerStyle?.color ?? d.colors.subtle}`,
          fontSize: pt(d.branding.footerStyle?.sizePt ?? 8),
          fontWeight: d.branding.footerStyle?.bold ? 700 : 400,
          fontFamily: fontStack(d.branding.footerStyle?.font ?? d.fonts.body),
          // Rule defaults ON (mirrors buildFooter in docx.ts), skipped when
          // there are no footer lines to sit under it.
          borderTop:
            d.branding.footerStyle?.showRule !== false && d.branding.footerLines.length > 0
              ? `${pt(d.branding.footerStyle?.ruleWidthPt ?? 1)}px solid #${d.branding.footerStyle?.color ?? d.colors.subtle}`
              : undefined,
          paddingTop:
            d.branding.footerStyle?.showRule !== false && d.branding.footerLines.length > 0
              ? pt(d.branding.footerStyle?.ruleSpacingPt ?? 4)
              : 0,
        }}
      >
        {d.branding.footerLines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
    </>
  );

  const items = appendixItems(appendix);
  // A text view is as tall as its content; measured so the scaled
  // container can make room for it.
  const [textHeights, setTextHeights] = useState<Record<string, number>>({});
  const textRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useLayoutEffect(() => {
    const observers: ResizeObserver[] = [];
    for (const [id, el] of Object.entries(textRefs.current)) {
      if (!el) continue;
      const obs = new ResizeObserver(() => {
        const height = el.getBoundingClientRect().height / (scale || 1);
        setTextHeights((prev) => (Math.abs((prev[id] ?? 0) - height) < 1 ? prev : { ...prev, [id]: height }));
      });
      obs.observe(el);
      observers.push(obs);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [items.length, scale]);
  const appendixPageCount = items.filter((it) => it.kind === "page" || it.kind === "text").length;
  const totalPages = pages.length + appendixPageCount;
  const geometry: PageGeometry = {
    pageW: PAGE_W,
    pageH: PAGE_H,
    marginLeft: tw(d.page.marginLeft),
    marginTop: tw(d.page.marginTop),
    contentW,
    capacity: PAGE_H - tw(d.page.marginTop) - tw(d.page.marginBottom),
  };
  const itemHeight = (it: AppendixItem) =>
    (it.kind === "removed" ? REMOVED_BAR_HEIGHT : it.kind === "text" ? Math.max(PAGE_H, textHeights[it.source.id] ?? 0) : PAGE_H) + PAGE_GAP;
  const totalH = (pages.length * (PAGE_H + PAGE_GAP) + items.reduce((s, it) => s + itemHeight(it), 0)) * scale;

  let appendixPageNo = pages.length;
  const appendixNodes = items.map((it) => {
    const k = `${it.source.id}:${it.kind === "loading" || it.kind === "text" ? it.kind : it.index}`;
    const wrap = (node: React.ReactNode) => (
      <div key={k} style={{ marginBottom: PAGE_GAP }}>
        {node}
      </div>
    );
    if (it.kind === "loading") return wrap(<LoadingAppendixPage source={it.source} geometry={geometry} />);
    if (it.kind === "text") {
      appendixPageNo += 1;
      return (
        <div key={k} style={{ marginBottom: PAGE_GAP }} ref={(el) => (textRefs.current[it.source.id] = el)}>
          <SourceTextView
            source={{ id: it.source.id, filename: it.source.filename, doc: it.source.text!.doc, textEdits: it.source.text!.textEdits, onChange: it.source.text!.onChange }}
            geometry={geometry}
            chrome={pageChrome(appendixPageNo - 1)}
            label={`${appendixPageNo} / ${totalPages}`}
          />
        </div>
      );
    }
    if (it.kind === "removed") {
      return wrap(
        <RemovedPageBar
          source={it.source}
          pageIndex={it.index}
          geometry={geometry}
          onRestore={
            it.source.onChange
              ? () =>
                  it.source.onChange!({
                    ...it.source.redactions,
                    removedPages: it.source.redactions.removedPages.filter((p) => p !== it.index),
                  })
              : undefined
          }
        />,
      );
    }
    appendixPageNo += 1;
    const page = it.source.pages!.find((p) => p.index === it.index)!;
    return wrap(
      <AppendixPage
        source={it.source}
        page={page}
        geometry={geometry}
        chrome={pageChrome(appendixPageNo - 1)}
        label={`${appendixPageNo} / ${totalPages}`}
        onRemovePage={
          it.source.onChange
            ? () =>
                it.source.onChange!({
                  ...it.source.redactions,
                  removedPages: [...it.source.redactions.removedPages, it.index].sort((a, b) => a - b),
                })
            : undefined
        }
      />,
    );
  });

  return (
    <div ref={wrapRef} className="max-h-[75vh] w-full overflow-x-auto overflow-y-auto rounded-lg">
      {/* Hidden measuring copy at full (unscaled) content width. The
          zero-height clipping wrapper is load-bearing: without it the
          absolutely-positioned copy anchors to the document and silently
          extends the PAGE's scroll height by the whole unscaled resume
          (thousands of pixels of phantom scroll below the footer). */}
      <div aria-hidden style={{ position: "relative", height: 0, overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            width: contentW,
            left: 0,
            top: 0,
            ...bodyStyle,
          }}
          ref={measureRef}
        >
          {staticBlocks.map((b, i) => (
            <div key={i}>{b.node}</div>
          ))}
        </div>
      </div>

      <div style={{ height: totalH, width: PAGE_W * scale }} className="mx-auto overflow-hidden">
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <EditSessionContext.Provider value={editCtx ? session : null}>
            <div ref={visibleRef} data-preview-pages="" onKeyDown={onRootKeyDown}>
              {pages.map((pageBlocks, pi) => (
                <div
                  key={pi}
                  data-preview-page={pi}
                  style={{
                    width: PAGE_W,
                    height: PAGE_H,
                    background: "#ffffff",
                    position: "relative",
                    marginBottom: PAGE_GAP,
                    boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                    ...bodyStyle,
                  }}
                >
                  {pageChrome(pi)}
                  <div
                    style={{
                      position: "absolute",
                      // Only page 1 can be pushed down by an inline logo; later
                      // pages have no logo and start at the top margin.
                      top: pi === 0 ? contentTop : tw(d.page.marginTop),
                      left: tw(d.page.marginLeft),
                      width: contentW,
                      maxHeight: capacity,
                      overflow: "hidden",
                    }}
                  >
                    {pageBlocks.map((bi) => (
                      <div key={bi}>{liveBlocks[bi]?.node}</div>
                    ))}
                  </div>
                  <span
                    style={{
                      position: "absolute",
                      right: 10,
                      bottom: 6,
                      fontFamily: "system-ui, sans-serif",
                      fontSize: 9,
                      color: "#B0B3B8",
                    }}
                  >
                    {pi + 1} / {totalPages}
                  </span>
                </div>
              ))}
            </div>
            <div style={bodyStyle} data-preview-appendix="">
              {appendixNodes}
            </div>
          </EditSessionContext.Provider>
        </div>
      </div>
    </div>
  );
}
